import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { FadeUp, MaskedLines } from "@/components/Reveal";

const PARTICIPANTS = [
  {
    id: "Participant 1",
    note: "before and after picture · results not guaranteed",
    photo: "/results-participant-1.webp",
  },
  {
    id: "Participant 2",
    note: "before and after picture · results not guaranteed",
    photo: "/results-participant-2.webp",
  },
  {
    id: "Participant 3",
    note: "before and after picture · results not guaranteed",
    photo: "/results-participant-3.webp",
    zoom: true,
  },
];

const placeholderStyle = {
  backgroundColor: "#E4EFE6",
  backgroundImage:
    "repeating-linear-gradient(45deg, rgba(27,67,50,0.05) 0 12px, transparent 12px 24px)",
};

function PlaceholderHalf({ label }) {
  return (
    <div
      className="flex-1 h-full grid place-items-center"
      style={placeholderStyle}
      data-testid={`results-${label.split(" ")[0].toLowerCase()}-placeholder`}
    >
      <p className="text-sm text-[#4A6B56] text-center leading-relaxed">
        {label.split(" ").map((word, i) => (
          <span key={i} className="block">
            {word}
          </span>
        ))}
      </p>
    </div>
  );
}

export default function Results() {
  return (
    <div data-testid="page-results">
      {/* ---------- Results that speak (up top) ---------- */}
      <section className="mx-auto max-w-[88rem] px-5 sm:px-10 pt-[7.5rem] lg:pt-[8.5rem] pb-8 lg:pb-10">
        <FadeUp>
          <div className="rounded-2xl bg-[#E4EFE6] px-8 py-8 sm:px-14 sm:py-11">
            <p className="text-xs uppercase tracking-[0.26em] text-[#4A6B56] mb-4">
              Real stories, real faces
            </p>
            <h1 className="font-display text-[clamp(2.4rem,5vw,4.4rem)] leading-[1.05] text-[#1B4332] max-w-4xl">
              <MaskedLines onView lines={["Results that speak", "for themselves."]} />
            </h1>
            <FadeUp delay={0.2}>
              <p className="mt-5 max-w-2xl text-lg text-[#4A6B56] leading-relaxed">
                Real faces, real mornings, real before-and-afters — shared by the people who drink
                Vibe every day.
              </p>
            </FadeUp>
          </div>
        </FadeUp>
      </section>

      {/* ---------- Before & After (below) ---------- */}
      <section className="mx-auto max-w-[88rem] px-5 sm:px-10 pb-12 lg:pb-16">
        <FadeUp className="mb-10">
          <p className="text-xs uppercase tracking-[0.26em] text-[#4A6B56] mb-4">Before & after</p>
          <h2 className="font-display text-[clamp(2.2rem,4.2vw,3.6rem)] leading-[1.05] text-[#1B4332]">
            Visible change, honestly shown.
          </h2>
        </FadeUp>

        <div className="grid md:grid-cols-3 gap-5">
          {PARTICIPANTS.map((participant, i) => (
            <FadeUp key={participant.id} delay={i * 0.1} className="h-full">
              <div
                data-testid={`results-card-${i + 1}`}
                className="rounded-2xl bg-white border border-[#D1E2D6]/60 overflow-hidden h-full flex flex-col"
              >
                <div className="aspect-[3/2] w-full overflow-hidden bg-[#E4EFE6]">
                  {participant.photo ? (
                    <img
                      src={participant.photo}
                      alt={`${participant.id} before and after`}
                      loading="lazy"
                      decoding="async"
                      className={`w-full h-full object-cover object-bottom ${
                        participant.zoom ? "scale-[1.08] origin-bottom" : ""
                      }`}
                      data-testid={`results-photo-card-${i + 1}`}
                    />
                  ) : (
                    <div className="flex h-full">
                      <PlaceholderHalf label="Before photo placeholder" />
                      <PlaceholderHalf label="After photo placeholder" />
                    </div>
                  )}
                </div>
                <p className="px-5 py-4 text-sm text-[#4A6B56] leading-relaxed">
                  <span className="font-semibold text-[#1B4332]">{participant.id}</span> ·{" "}
                  {participant.note}
                </p>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* ---------- Link to How to use ---------- */}
      <section className="mx-auto max-w-[88rem] px-5 sm:px-10 pb-24 lg:pb-36">
        <FadeUp>
          <div className="rounded-2xl bg-[#F1F8F2] border border-[#D1E2D6]/60 p-10 sm:p-14 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-10">
            <div>
              <h2 className="font-display text-[clamp(1.7rem,2.8vw,2.4rem)] leading-[1.15] text-[#1B4332] max-w-xl">
                How to use
              </h2>
              <p className="mt-3 max-w-md text-[#4A6B56] leading-relaxed">
                A simple morning routine — one sachet, mixed with water, with or after breakfast.
              </p>
            </div>
            <Link
              to="/routine"
              data-testid="results-routine-link"
              className="group inline-flex items-center gap-2.5 rounded-full bg-[#1B4332] text-[#E8F3EB] px-8 h-13 min-h-[3.25rem] font-medium hover:bg-[#2D5A44] transition-colors duration-300 shrink-0"
            >
              Open how to use
              <ArrowUpRight
                size={17}
                className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </Link>
          </div>
        </FadeUp>
      </section>
    </div>
  );
}
